import { Dispatch, SetStateAction } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteTrackingFile } from "api";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import Button from "components/v2/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";
import { DeletebleFileType } from "./ActivityFilesTypes";

interface RemoveMessageDialogProps {
  isOpen: boolean;
  close: () => void;
  shipmentId: string;
  selectedFile: DeletebleFileType;
  setFilesBeingDeletedByUpdateId: Dispatch<SetStateAction<string[]>>;
}

const DeleteTrackingFileDialog = function ({
  isOpen,
  close,
  shipmentId,
  selectedFile,
  setFilesBeingDeletedByUpdateId,
}: RemoveMessageDialogProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const config = {
    params: {
      attachment_only: true,
      attachment_url: selectedFile.url,
    },
  };

  const { mutate, isLoading: isDeleting } = useMutation({
    mutationFn: () => deleteTrackingFile(selectedFile.updateId, config),
    onSuccess: async () => {
      toast(
        intl.formatMessage({
          id: "tracking.activity.deleteFileDialog.deleteSuccess",
          defaultMessage: "File successfully deleted.",
        }),
        { type: "success" },
      );
      close();
      setFilesBeingDeletedByUpdateId((prev) => prev.filter((id) => id !== selectedFile.updateId));
      await new Promise((resolve) => {
        setTimeout(resolve, 750);
      }).then(() =>
        queryClient.invalidateQueries({
          queryKey: shipmentQueryKeys.shipmentGeoDataDetails(shipmentId),
        }),
      );
    },
    onError: (err) => {
      setFilesBeingDeletedByUpdateId((prev) => prev.filter((id) => id !== selectedFile.updateId));
      errorToast(
        err,
        intl.formatMessage({
          id: "tracking.activity.deleteFileDialog.deleteFail",
          defaultMessage: "Could not delete file. Please try again later.",
        }),
      );
    },
    onMutate: () => {
      setFilesBeingDeletedByUpdateId((prev) => [...prev, selectedFile.updateId]);
    },
  });

  return (
    <Dialog
      show={isOpen}
      title={intl.formatMessage({
        id: "tracking.activity.deleteFileDialog.title",
        defaultMessage: "Delete File?",
      })}
      onHide={close}
    >
      <div className="text-sm text-secondary">
        {intl.formatMessage({
          id: "tracking.activity.deleteFileDialog.subtitle",
          defaultMessage: "This file will be deleted for everyone.",
        })}
      </div>
      <div className="d-flex justify-content-end gap-lg mt-3xl">
        <Button
          disabled={isDeleting}
          variant="secondary-gray"
          label={intl.formatMessage({
            id: "general.back",
            defaultMessage: "Back",
          })}
          onClick={close}
        />
        <Button
          disabled={isDeleting}
          onClick={mutate}
          variant="error"
          label={intl.formatMessage({
            id: "general.delete",
            defaultMessage: "Delete",
          })}
        />
      </div>
    </Dialog>
  );
};

export default DeleteTrackingFileDialog;
